import { Button } from "@gigpro/ui/Button";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Label } from "@gigpro/ui/Label";
import { RadioGroup, RadioGroupItem } from "@gigpro/ui/RadioGroup";
import { Textarea } from "@gigpro/ui/Textarea";
import type { GigOutreachReasonEnum } from "@sidegigllc/api/client";
import React from "react";

/** DIRECT_OFFER creates an offer per pro; SHIFT_INVITE grants each pro the right to claim, first to confirm wins. */
export type DirectOfferSendMode = "DIRECT_OFFER" | "SHIFT_INVITE";

export interface DirectOfferNote {
  reason: GigOutreachReasonEnum;
  note: string;
  /** Set only when the choice was shown, so callers that never offer it send no extra field. */
  mode?: DirectOfferSendMode;
}

interface DirectOfferNoteDialogProps {
  open: boolean;
  /** Who the offer goes to, e.g. "Ana B." or "37 pros". */
  recipients: string;
  onCancel: () => void;
  onConfirm: (note: DirectOfferNote) => void;
  /** Offer the send-type choice (big sends in markets not enrolled in DO grants). */
  showModeChoice?: boolean;
}

const MODES: ReadonlyArray<[DirectOfferSendMode, string, string]> = [
  [
    "SHIFT_INVITE",
    "Send shift invites",
    "Every pro can claim the shift until it fills. The first to confirm gets it, and nobody is left holding an offer.",
  ],
  [
    "DIRECT_OFFER",
    "Send direct offers",
    "Every pro gets an offer to accept or decline. Each one stays pending until they answer or the shift fills.",
  ],
];

const REASONS: ReadonlyArray<[GigOutreachReasonEnum, string]> = [
  ["VENUE_REQUESTED", "Venue asked for these pros"],
  ["NOT_OPEN_TO_EVERYONE", "Shift isn't open to everyone"],
  ["LAST_CALL_DID_NOT_FILL", "Last Call didn't fill it"],
  ["PRO_REQUESTED", "Pro asked for this shift"],
  ["OTHER", "Other"],
];
const NOTE_MIN_LENGTH = 10;
const NOTE_MAX_LENGTH = 500;

export function DirectOfferNoteDialog(props: Readonly<DirectOfferNoteDialogProps>) {
  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DirectOfferNoteDialogBody {...props} />
    </Dialog>
  );
}

function DirectOfferNoteDialogBody({
  recipients,
  onCancel,
  onConfirm,
  showModeChoice = false,
}: Readonly<DirectOfferNoteDialogProps>) {
  // Direct offers stay the default; shift invites are offered as the recommended alternative.
  const [mode, setMode] = React.useState<DirectOfferSendMode>("DIRECT_OFFER");
  const [reason, setReason] = React.useState<GigOutreachReasonEnum | null>(null);
  const [note, setNote] = React.useState("");

  const noteLength = note.trim().length;
  const canSend = reason !== null && noteLength >= NOTE_MIN_LENGTH;

  const onSend = () => {
    if (reason === null || !canSend) return;
    onConfirm({ reason, note: note.trim(), ...(showModeChoice && { mode }) });
  };

  return (
    <>
      <DialogTitle>
        {mode === "SHIFT_INVITE"
          ? `Invite ${recipients} to claim this shift?`
          : `Send a direct offer to ${recipients}?`}
      </DialogTitle>
      {showModeChoice && (
        <RadioGroup
          aria-label="How to reach these pros"
          className="gap-2 py-2"
          value={mode}
          onValueChange={(value) => setMode(value as DirectOfferSendMode)}
        >
          {MODES.map(([value, label, detail]) => (
            <ModeOption key={value} value={value} label={label} detail={detail} />
          ))}
        </RadioGroup>
      )}
      <p className="text-normal text-gray">Why reach out for this shift? Your answer is saved with the send.</p>
      <RadioGroup
        aria-label="Reason for the direct offer"
        className="py-2"
        value={reason ?? ""}
        onValueChange={(value) => setReason(value as GigOutreachReasonEnum)}
      >
        {REASONS.map(([value, label]) => (
          <ReasonOption key={value} value={value} label={label} />
        ))}
      </RadioGroup>
      <Textarea
        aria-label="Note"
        placeholder="e.g. GM asked for Ana and Ben by name"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={NOTE_MAX_LENGTH}
        required
      />
      <p className="text-small text-gray">
        {noteLength < NOTE_MIN_LENGTH
          ? `At least ${NOTE_MIN_LENGTH} characters (${noteLength}/${NOTE_MIN_LENGTH})`
          : `${note.length}/${NOTE_MAX_LENGTH}`}
      </p>
      <DialogFooter>
        <Button className="w-full" onClick={onCancel}>
          Back
        </Button>
        <Button className="w-full" variant="contained" onClick={onSend} disabled={!canSend}>
          Continue
        </Button>
      </DialogFooter>
    </>
  );
}

function ReasonOption({ value, label }: Readonly<{ value: GigOutreachReasonEnum; label: string }>) {
  const id = `direct-offer-reason-${value}`;
  return (
    <div className="flex items-center">
      <RadioGroupItem id={id} value={value} />
      <Label htmlFor={id} className="ml-2 text-normal font-semibold">
        {label}
      </Label>
    </div>
  );
}

function ModeOption({ value, label, detail }: Readonly<{ value: DirectOfferSendMode; label: string; detail: string }>) {
  const id = `direct-offer-mode-${value}`;
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer gap-3 rounded-lg border border-gray/30 p-3 has-data-[state=checked]:border-brand has-data-[state=checked]:bg-brand/10"
    >
      <RadioGroupItem id={id} value={value} className="mt-1" />
      <span>
        <span className="block text-normal font-semibold">
          {label}
          {value === "SHIFT_INVITE" && <span className="ml-2 text-small font-regular text-brand">Recommended</span>}
        </span>
        <span className="block text-small text-gray">{detail}</span>
      </span>
    </label>
  );
}
